import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, ArrowLeft, RotateCcw, ChevronDown, PenLine } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { api } from "../../services/api";
import TarotFanSpread, { TarotFanSpreadTheme } from "../Tarot/TarotFanSpread";
import { TarotCard, tarotCards } from "../../utils/tarotCards";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";

// Astria Tarot — card-spread reading for /jp-tarot, shared across all 11
// supported locales (JP/KR/TH/EN bridged tones, standalone Brazil pack,
// standalone 6-lane pack — see the Locale/BR_LOCALES/LANE_LOCALES routing
// below). One universal visual design (violet accent on a light neutral
// ground) instead of a
// per-locale theme, so it reads as considered for every language rather
// than one country's aesthetic. Reuses the shared TarotFanSpread picker UI
// for the actual card-draw mechanic; only this page's shell, step rail,
// picker, and result screen are themed here.

const C = {
  bg: "#F5F3FB",
  panel: "#FFFFFF",
  panelAlt: "#F7F5FC",
  line: "rgba(30,20,70,0.09)",
  lineStrong: "rgba(30,20,70,0.15)",
  text: "#211A3D",
  muted: "#6E6690",
  accent: "#7C5CFF",
  accentDeep: "#5A3FE0",
  accentSoft: "rgba(124,92,255,0.12)",
  error: "#D65C4C",
};
const FONT_DISPLAY = "'Fraunces', 'Noto Serif JP', serif";
const FONT_BODY =
  "'Inter', 'Noto Sans JP', 'Noto Sans Arabic', 'Noto Sans', sans-serif";
const FONT = FONT_BODY;

// Restyles TarotFanSpread's "question" card to match this page's violet
// design system instead of the shared component's default purple theme.
// Defined at module scope so the reference stays stable across renders.
const FAN_SPREAD_THEME: TarotFanSpreadTheme = {
  cardBg: C.panelAlt,
  cardBorder: C.line,
  titleColor: C.text,
  titleFont: FONT_DISPLAY,
  bodyFont: FONT_BODY,
  inputBorder: C.lineStrong,
  inputFocusRing: C.accent,
  inputText: C.text,
  placeholderColor: C.muted,
  buttonGradient: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDeep} 100%)`,
  buttonShadow: `0 8px 20px -8px ${C.accentDeep}66`,
  mutedColor: C.muted,
  borderRadius: 14,
};

type Category = "general" | "romance" | "healing";
type QuestionType =
  | "message_now"
  | "future_event"
  | "self_charm"
  | "guardian_message"
  | "self_reflection"
  | "guidance" // pt-BR general ids (helper/brTarot/brTarotPacks.js)
  | "their_feelings"
  | "relationship_flow"
  | "contact_timing"
  | "emotional_state"
  | "energy_block"
  | "recovery_flow";

// Matches backend SUPPORTED_LOCALES (tarotLocalePacks.js) — JP is the
// prediction-pack source locale, KR/TH/EN are bridged tones. pt-BR is a
// standalone pack (helper/brTarot/*, mounted at /api/backend/br-tarot) rather
// than a bridged tone, so it's routed to its own endpoint in handleGetReading.
// es/ar/fil/id/ms/vi are the "Astria Tarot Lanes" (helper/astriaTarotLanes/*,
// mounted at /api/backend/tarot-lanes) — also standalone, LLM-only packs;
// see LANE_LOCALES below.
type Locale =
  | "ja-JP"
  | "ko-KR"
  | "th-TH"
  | "en-US"
  | "pt-BR"
  | "es"
  | "ar"
  | "fil"
  | "id"
  | "ms"
  | "vi";

const LOCALES: Locale[] = [
  "ja-JP",
  "ko-KR",
  "th-TH",
  "en-US",
  "pt-BR",
  "es",
  "ar",
  "fil",
  "id",
  "ms",
  "vi",
];

// Locales served by the standalone Brazil Tarot module instead of the
// jp-tarot bridge (helper/brTarot/* on the backend).
const BR_LOCALES: ReadonlySet<Locale> = new Set<Locale>(["pt-BR"]);

// Locales served by the standalone 6-lane Astria Tarot module
// (helper/astriaTarotLanes/*, mounted at /api/backend/tarot-lanes).
const LANE_LOCALES: ReadonlySet<Locale> = new Set<Locale>([
  "es",
  "ar",
  "fil",
  "id",
  "ms",
  "vi",
]);

// Right-to-left locales — used to set dir="rtl" on rendered reading text.
const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(["ar"]);

const LOCALE_FLAG: Record<Locale, string> = {
  "ja-JP": "🇯🇵",
  "ko-KR": "🇰🇷",
  "th-TH": "🇹🇭",
  "en-US": "🇬🇧",
  "pt-BR": "🇧🇷",
  es: "🇪🇸",
  ar: "🇸🇦",
  fil: "🇵🇭",
  id: "🇮🇩",
  ms: "🇲🇾",
  vi: "🇻🇳",
};

// Full language name (in its own script), used as the language dropdown's
// selected value and its open-list options.
const LOCALE_FULL_LABEL: Record<Locale, string> = {
  "ja-JP": "日本語",
  "ko-KR": "한국어",
  "th-TH": "ไทย",
  "en-US": "English",
  "pt-BR": "Português (Brasil)",
  es: "Español",
  ar: "العربية",
  fil: "Filipino",
  id: "Bahasa Indonesia",
  ms: "Bahasa Melayu",
  vi: "Tiếng Việt",
};

interface BirthDetails {
  dob: string;
  birthTime: string;
  birthPlace: string;
  gender: string;
}

interface LocaleCopy {
  badge: string;
  badgeSub: string;
  categoryLabel: Record<Category, string>;
  questionLabel: Record<QuestionType, string>;
  stepLabel: Record<Step, string>;
  heading: string;
  subheading: string;
  categoryHeader: string;
  questionHeader: string;
  chooseCardsButton: string;
  backButton: string;
  askAgainButton: string;
  defaultQuestionPrompt: string;
  fallbackErrorMessage: string;
  overallMessageLabel: string;
  reflectionLabel: string;
  reflectionPrompt: string;
  reversedLabel: string;
}

type Step = "questionType" | "cards" | "result";

const STEP_ORDER: Step[] = ["questionType", "cards", "result"];

const COPY: Record<Locale, LocaleCopy> = {
  "ja-JP": {
    badge: "JP TAROT",
    badgeSub: "タロット占い",
    categoryLabel: { general: "総合運", romance: "恋愛運", healing: "癒し" },
    questionLabel: {
      message_now: "今必要なメッセージ",
      future_event: "近いうちに起こること",
      self_charm: "あなたの魅力・印象",
      guardian_message: "守護からのメッセージ",
      self_reflection: "内省のひととき",
      guidance: "今のアドバイス",
      their_feelings: "相手の今の気持ち",
      relationship_flow: "関係の流れ",
      contact_timing: "連絡のタイミング",
      emotional_state: "感情の状態",
      energy_block: "エネルギーの滞り",
      recovery_flow: "回復の流れ",
    },
    stepLabel: { questionType: "意図", cards: "カード選択", result: "結果" },
    heading: "あなたへの静かな占い",
    subheading: "今日、導きを求めたいことを選んでください。",
    categoryHeader: "カテゴリー",
    questionHeader: "質問",
    chooseCardsButton: "カードを選ぶ",
    backButton: "戻る",
    askAgainButton: "もう一度占う",
    defaultQuestionPrompt: "占ってください。",
    fallbackErrorMessage: "うまくいきませんでした。もう一度お試しください。",
    overallMessageLabel: "総合メッセージ",
    reflectionLabel: "内省",
    reflectionPrompt: "今、心に留めておきたいことは何ですか？",
    reversedLabel: "逆位置",
  },
  "ko-KR": {
    badge: "KR TAROT",
    badgeSub: "타로 점",
    categoryLabel: { general: "종합운", romance: "연애운", healing: "치유" },
    questionLabel: {
      message_now: "지금 필요한 메시지",
      future_event: "곧 일어날 일",
      self_charm: "당신의 매력 / 인상",
      guardian_message: "수호자로부터의 메시지",
      self_reflection: "잠시 멈추어 나를 돌아보기",
      guidance: "지금 필요한 조언",
      their_feelings: "상대방의 현재 감정",
      relationship_flow: "관계의 흐름",
      contact_timing: "연락 타이밍",
      emotional_state: "감정 상태",
      energy_block: "에너지 정체",
      recovery_flow: "회복의 흐름",
    },
    stepLabel: { questionType: "의도", cards: "카드 선택", result: "결과" },
    heading: "당신을 위한 조용한 리딩",
    subheading: "오늘 어떤 안내를 받고 싶은지 선택해 주세요.",
    categoryHeader: "카테고리",
    questionHeader: "질문",
    chooseCardsButton: "카드 선택하기",
    backButton: "뒤로",
    askAgainButton: "다시 묻기",
    defaultQuestionPrompt: "점을 봐주세요.",
    fallbackErrorMessage: "문제가 발생했습니다. 다시 시도해 주세요.",
    overallMessageLabel: "종합 메시지",
    reflectionLabel: "성찰",
    reflectionPrompt: "지금 마음에 새겨두고 싶은 것은 무엇인가요?",
    reversedLabel: "역방향",
  },
  "th-TH": {
    badge: "TH TAROT",
    badgeSub: "ไพ่ทาโรต์",
    categoryLabel: {
      general: "ภาพรวม",
      romance: "ความรัก",
      healing: "การเยียวยา",
    },
    questionLabel: {
      message_now: "ข้อความที่คุณต้องการตอนนี้",
      future_event: "สิ่งที่จะเกิดขึ้นเร็วๆ นี้",
      self_charm: "เสน่ห์ / ความประทับใจของคุณ",
      guardian_message: "ข้อความจากผู้พิทักษ์",
      self_reflection: "ช่วงเวลาทบทวนตัวเอง",
      guidance: "คำแนะนำสำหรับตอนนี้",
      their_feelings: "ความรู้สึกของอีกฝ่ายตอนนี้",
      relationship_flow: "แนวโน้มความสัมพันธ์",
      contact_timing: "จังหวะการติดต่อ",
      emotional_state: "สภาวะอารมณ์",
      energy_block: "พลังงานที่ติดขัด",
      recovery_flow: "แนวโน้มการฟื้นตัว",
    },
    stepLabel: {
      questionType: "ความตั้งใจ",
      cards: "เลือกไพ่",
      result: "ผลลัพธ์",
    },
    heading: "การทำนายที่สงบสำหรับคุณ",
    subheading: "เลือกสิ่งที่คุณอยากได้รับคำแนะนำในวันนี้",
    categoryHeader: "หมวดหมู่",
    questionHeader: "คำถาม",
    chooseCardsButton: "เลือกไพ่ของคุณ",
    backButton: "ย้อนกลับ",
    askAgainButton: "ถามอีกครั้ง",
    defaultQuestionPrompt: "กรุณาทำนายให้หน่อยค่ะ",
    fallbackErrorMessage: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
    overallMessageLabel: "ข้อความโดยรวม",
    reflectionLabel: "การใคร่ครวญ",
    reflectionPrompt: "สิ่งใดที่คุณอยากให้ความสำคัญที่สุดตอนนี้",
    reversedLabel: "กลับหัว",
  },
  "en-US": {
    badge: "TAROT",
    badgeSub: "Tarot Reading",
    categoryLabel: {
      general: "General",
      romance: "Romance",
      healing: "Healing",
    },
    questionLabel: {
      message_now: "Message you need now",
      future_event: "What will happen soon",
      self_charm: "Your charm / impression",
      guardian_message: "Message from your guardian",
      self_reflection: "A moment of self-reflection",
      guidance: "Guidance for right now",
      their_feelings: "Their current feelings",
      relationship_flow: "Relationship flow",
      contact_timing: "Contact timing",
      emotional_state: "Emotional state",
      energy_block: "Energy block",
      recovery_flow: "Recovery flow",
    },
    stepLabel: { questionType: "Intention", cards: "Draw", result: "Reading" },
    heading: "A quiet reading for you",
    subheading: "Choose what you'd like guidance on today.",
    categoryHeader: "Category",
    questionHeader: "Question",
    chooseCardsButton: "Choose Your Cards",
    backButton: "Back",
    askAgainButton: "Ask Again",
    defaultQuestionPrompt: "Please give me a reading.",
    fallbackErrorMessage: "Something went wrong. Please try again.",
    overallMessageLabel: "Overall Message",
    reflectionLabel: "Reflection",
    reflectionPrompt: "What feels most important for you to acknowledge right now?",
    reversedLabel: "Reversed",
  },
  "pt-BR": {
    badge: "TARÔ BRASIL",
    badgeSub: "Leitura de Tarô",
    categoryLabel: {
      general: "Geral",
      romance: "Amor",
      healing: "Autocuidado",
    },
    questionLabel: {
      message_now: "Mensagem que você precisa agora",
      future_event: "O que pode acontecer em breve",
      self_charm: "Seu magnetismo / impressão",
      guardian_message: "Mensagem de orientação",
      self_reflection: "Um momento de autorreflexão",
      guidance: "Um conselho para o momento",
      their_feelings: "O que a outra pessoa sente agora",
      relationship_flow: "Como a relação está fluindo",
      contact_timing: "Quando pode haver contato",
      emotional_state: "Seu estado emocional",
      energy_block: "O que pode estar te travando",
      recovery_flow: "Como está sua recuperação",
    },
    stepLabel: {
      questionType: "Intenção",
      cards: "Escolher cartas",
      result: "Leitura",
    },
    heading: "Uma leitura tranquila para você",
    subheading: "Escolha sobre o que você gostaria de clareza hoje.",
    categoryHeader: "Categoria",
    questionHeader: "Pergunta",
    chooseCardsButton: "Escolha suas cartas",
    backButton: "Voltar",
    askAgainButton: "Perguntar novamente",
    defaultQuestionPrompt: "Por favor, faça a leitura das cartas.",
    fallbackErrorMessage: "Algo não deu certo. Por favor, tente novamente.",
    overallMessageLabel: "Mensagem geral",
    reflectionLabel: "Reflexão",
    reflectionPrompt: "O que parece mais importante para você reconhecer agora?",
    reversedLabel: "Invertida",
  },
  es: {
    badge: "TAROT",
    badgeSub: "Lectura de Tarot",
    categoryLabel: { general: "General", romance: "Amor", healing: "Sanación" },
    questionLabel: {
      message_now: "Mensaje que necesitas ahora",
      future_event: "Lo que podría suceder pronto",
      self_charm: "Tu magnetismo / impresión",
      guardian_message: "Mensaje de orientación",
      self_reflection: "Un momento de reflexión",
      guidance: "Un consejo para ahora",
      their_feelings: "Lo que la otra persona siente ahora",
      relationship_flow: "Cómo fluye la relación",
      contact_timing: "Cuándo podría haber contacto",
      emotional_state: "Tu estado emocional",
      energy_block: "Qué te puede estar frenando",
      recovery_flow: "Cómo va tu recuperación",
    },
    stepLabel: { questionType: "Intención", cards: "Elegir cartas", result: "Lectura" },
    heading: "Una lectura tranquila para ti",
    subheading: "Elige sobre qué te gustaría tener claridad hoy.",
    categoryHeader: "Categoría",
    questionHeader: "Pregunta",
    chooseCardsButton: "Elige tus cartas",
    backButton: "Volver",
    askAgainButton: "Preguntar de nuevo",
    defaultQuestionPrompt: "Por favor, dame una lectura de las cartas.",
    fallbackErrorMessage: "Algo salió mal. Por favor, inténtalo de nuevo.",
    overallMessageLabel: "Mensaje general",
    reflectionLabel: "Reflexión",
    reflectionPrompt: "¿Qué te parece más importante reconocer ahora mismo?",
    reversedLabel: "Invertida",
  },
  ar: {
    badge: "التاروت",
    badgeSub: "قراءة التاروت",
    categoryLabel: { general: "عام", romance: "الحب", healing: "الشفاء" },
    questionLabel: {
      message_now: "الرسالة التي تحتاجها الآن",
      future_event: "ما قد يحدث قريبًا",
      self_charm: "جاذبيتك / انطباعك",
      guardian_message: "رسالة إرشاد",
      self_reflection: "لحظة تأمل ذاتي",
      guidance: "نصيحة لهذه اللحظة",
      their_feelings: "ما يشعر به الشخص الآخر الآن",
      relationship_flow: "كيف تسير العلاقة",
      contact_timing: "متى قد يحدث التواصل",
      emotional_state: "حالتك العاطفية",
      energy_block: "ما قد يعيقك",
      recovery_flow: "كيف يسير تعافيك",
    },
    stepLabel: { questionType: "النية", cards: "اختيار البطاقات", result: "القراءة" },
    heading: "قراءة هادئة من أجلك",
    subheading: "اختر ما تريد أن تحصل على وضوح بشأنه اليوم.",
    categoryHeader: "الفئة",
    questionHeader: "السؤال",
    chooseCardsButton: "اختر بطاقاتك",
    backButton: "رجوع",
    askAgainButton: "اسأل مرة أخرى",
    defaultQuestionPrompt: "من فضلك أعطني قراءة للبطاقات.",
    fallbackErrorMessage: "حدث خطأ ما. من فضلك حاول مرة أخرى.",
    overallMessageLabel: "الرسالة العامة",
    reflectionLabel: "تأمل",
    reflectionPrompt: "ما الذي تشعر أنه الأهم أن تتقبّله الآن؟",
    reversedLabel: "مقلوبة",
  },
  fil: {
    badge: "TAROT",
    badgeSub: "Pagbasa ng Tarot",
    categoryLabel: { general: "Pangkalahatan", romance: "Pag-ibig", healing: "Pagpapagaling" },
    questionLabel: {
      message_now: "Mensaheng kailangan mo ngayon",
      future_event: "Ang maaaring mangyari sa malapit na hinaharap",
      self_charm: "Ang iyong akit / impresyon",
      guardian_message: "Mensahe ng gabay",
      self_reflection: "Isang sandali ng pagninilay",
      guidance: "Payo para sa ngayon",
      their_feelings: "Nararamdaman ng ibang tao ngayon",
      relationship_flow: "Kung paano umaagos ang relasyon",
      contact_timing: "Kailan maaaring magkaroon ng contact",
      emotional_state: "Ang iyong emosyonal na kalagayan",
      energy_block: "Ang maaaring humahadlang sa iyo",
      recovery_flow: "Kung paano ang iyong paggaling",
    },
    stepLabel: { questionType: "Layunin", cards: "Pumili ng mga baraha", result: "Pagbasa" },
    heading: "Isang tahimik na pagbasa para sa iyo",
    subheading: "Piliin kung ano ang gusto mong linawin ngayon.",
    categoryHeader: "Kategorya",
    questionHeader: "Tanong",
    chooseCardsButton: "Piliin ang iyong mga baraha",
    backButton: "Bumalik",
    askAgainButton: "Magtanong ulit",
    defaultQuestionPrompt: "Paki-basa naman ang mga baraha.",
    fallbackErrorMessage: "May nagkamali. Pakisubukang muli.",
    overallMessageLabel: "Pangkalahatang Mensahe",
    reflectionLabel: "Pagninilay",
    reflectionPrompt: "Ano ang pakiramdam mong pinakamahalagang tanggapin ngayon?",
    reversedLabel: "Baligtad",
  },
  id: {
    badge: "TAROT",
    badgeSub: "Pembacaan Tarot",
    categoryLabel: { general: "Umum", romance: "Cinta", healing: "Penyembuhan" },
    questionLabel: {
      message_now: "Pesan yang kamu butuhkan sekarang",
      future_event: "Apa yang mungkin terjadi segera",
      self_charm: "Daya tarik / kesanmu",
      guardian_message: "Pesan bimbingan",
      self_reflection: "Momen untuk merenung",
      guidance: "Saran untuk saat ini",
      their_feelings: "Apa yang dirasakan orang itu sekarang",
      relationship_flow: "Bagaimana hubungan mengalir",
      contact_timing: "Kapan mungkin ada kontak",
      emotional_state: "Keadaan emosionalmu",
      energy_block: "Apa yang mungkin menghambatmu",
      recovery_flow: "Bagaimana pemulihanmu",
    },
    stepLabel: { questionType: "Niat", cards: "Pilih kartu", result: "Pembacaan" },
    heading: "Pembacaan yang tenang untukmu",
    subheading: "Pilih apa yang ingin kamu perjelas hari ini.",
    categoryHeader: "Kategori",
    questionHeader: "Pertanyaan",
    chooseCardsButton: "Pilih kartumu",
    backButton: "Kembali",
    askAgainButton: "Tanya lagi",
    defaultQuestionPrompt: "Tolong berikan aku pembacaan kartu.",
    fallbackErrorMessage: "Ada yang salah. Silakan coba lagi.",
    overallMessageLabel: "Pesan Keseluruhan",
    reflectionLabel: "Perenungan",
    reflectionPrompt: "Apa yang terasa paling penting untuk kamu akui sekarang?",
    reversedLabel: "Terbalik",
  },
  ms: {
    badge: "TAROT",
    badgeSub: "Bacaan Tarot",
    categoryLabel: { general: "Umum", romance: "Cinta", healing: "Pemulihan" },
    questionLabel: {
      message_now: "Mesej yang anda perlukan sekarang",
      future_event: "Apa yang mungkin berlaku tidak lama lagi",
      self_charm: "Daya tarikan / kesan anda",
      guardian_message: "Mesej panduan",
      self_reflection: "Saat untuk merenung",
      guidance: "Nasihat untuk sekarang",
      their_feelings: "Apa yang dirasai orang itu sekarang",
      relationship_flow: "Bagaimana hubungan itu mengalir",
      contact_timing: "Bila mungkin ada hubungan",
      emotional_state: "Keadaan emosi anda",
      energy_block: "Apa yang mungkin menghalang anda",
      recovery_flow: "Bagaimana pemulihan anda",
    },
    stepLabel: { questionType: "Niat", cards: "Pilih kad", result: "Bacaan" },
    heading: "Bacaan yang tenang untuk anda",
    subheading: "Pilih apa yang anda mahu jelaskan hari ini.",
    categoryHeader: "Kategori",
    questionHeader: "Soalan",
    chooseCardsButton: "Pilih kad anda",
    backButton: "Kembali",
    askAgainButton: "Tanya lagi",
    defaultQuestionPrompt: "Tolong berikan saya bacaan kad.",
    fallbackErrorMessage: "Sesuatu tidak kena. Sila cuba lagi.",
    overallMessageLabel: "Mesej Keseluruhan",
    reflectionLabel: "Renungan",
    reflectionPrompt: "Apa yang paling penting untuk anda akui sekarang?",
    reversedLabel: "Terbalik",
  },
  vi: {
    badge: "TAROT",
    badgeSub: "Xem Bài Tarot",
    categoryLabel: { general: "Tổng quan", romance: "Tình yêu", healing: "Chữa lành" },
    questionLabel: {
      message_now: "Thông điệp bạn cần ngay bây giờ",
      future_event: "Điều có thể sắp xảy ra",
      self_charm: "Sức hút / ấn tượng của bạn",
      guardian_message: "Thông điệp dẫn dắt",
      self_reflection: "Một khoảnh khắc tự chiêm nghiệm",
      guidance: "Lời khuyên cho lúc này",
      their_feelings: "Cảm xúc hiện tại của người đó",
      relationship_flow: "Mối quan hệ đang diễn ra thế nào",
      contact_timing: "Khi nào có thể có liên lạc",
      emotional_state: "Trạng thái cảm xúc của bạn",
      energy_block: "Điều có thể đang cản trở bạn",
      recovery_flow: "Quá trình hồi phục của bạn",
    },
    stepLabel: { questionType: "Ý định", cards: "Chọn lá bài", result: "Kết quả" },
    heading: "Một buổi xem bài an yên dành cho bạn",
    subheading: "Chọn điều bạn muốn được làm rõ hôm nay.",
    categoryHeader: "Danh mục",
    questionHeader: "Câu hỏi",
    chooseCardsButton: "Chọn lá bài của bạn",
    backButton: "Quay lại",
    askAgainButton: "Hỏi lại",
    defaultQuestionPrompt: "Xin hãy xem bài giúp tôi.",
    fallbackErrorMessage: "Đã có lỗi xảy ra. Vui lòng thử lại.",
    overallMessageLabel: "Thông điệp tổng quát",
    reflectionLabel: "Chiêm nghiệm",
    reflectionPrompt: "Điều gì cảm thấy quan trọng nhất để bạn ghi nhận lúc này?",
    reversedLabel: "Ngược",
  },
};

// category -> question types, mirrors backend QUESTION_TYPES (jpTarotPacks.js).
// Shared by ja-JP/ko-KR/th-TH/en-US (the jp-tarot bridge).
const QUESTION_TYPES: Record<Category, QuestionType[]> = {
  general: ["message_now", "future_event", "self_charm", "guardian_message"],
  romance: ["their_feelings", "relationship_flow", "contact_timing"],
  healing: ["emotional_state", "energy_block", "recovery_flow"],
};

// pt-BR's and the 6 Astria Tarot lanes' general question ids differ from the
// JP set (mirrors backend QUESTION_TYPES in helper/brTarot/brTarotPacks.js
// and helper/astriaTarotLanes/tarotLanesPacks.js, which share this same
// general set) — romance/healing ids are shared across every locale.
const ALT_QUESTION_TYPES: Record<Category, QuestionType[]> = {
  ...QUESTION_TYPES,
  general: ["message_now", "future_event", "self_reflection", "guidance"],
};

const questionTypesForLocale = (
  locale: Locale,
): Record<Category, QuestionType[]> =>
  BR_LOCALES.has(locale) || LANE_LOCALES.has(locale)
    ? ALT_QUESTION_TYPES
    : QUESTION_TYPES;

// Pill-style step rail — each step is its own chip with a numbered badge,
// rather than a dot-and-line progress bar, so it wraps cleanly at phone
// width and reads correctly in both LTR and RTL locales (the flex row and
// each chip's internal gap flip automatically under dir="rtl").
const StepRail: React.FC<{ step: Step; stepLabel: Record<Step, string> }> = ({
  step,
  stepLabel,
}) => {
  const activeIdx = STEP_ORDER.indexOf(step);
  return (
    <div className="flex items-center justify-center gap-2 flex-wrap mb-6 sm:mb-7">
      {STEP_ORDER.map((s, idx) => {
        const isActive = idx === activeIdx;
        const isDone = idx < activeIdx;
        return (
          <div
            key={s}
            className="flex items-center gap-1.5 rounded-full transition-all duration-200"
            style={{
              padding: "6px 12px 6px 6px",
              border: `1px solid ${isActive ? C.accent : C.lineStrong}`,
              background: isActive ? C.accentSoft : C.panel,
            }}
          >
            <span
              className="flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 18,
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                fontFamily: FONT_BODY,
                background: isDone || isActive ? C.accent : C.line,
                color: isDone || isActive ? "#FFF" : C.muted,
              }}
            >
              {idx + 1}
            </span>
            <span
              className="whitespace-nowrap"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.03em",
                color: isActive ? C.text : C.muted,
                fontFamily: FONT_BODY,
              }}
            >
              {stepLabel[s]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface CardReadingResult {
  questionType: string;
  category: Category;
  tone:
    | "traditional_soft"
    | "hybrid_clear"
    | "ptbr_warm_intuitive"
    | "ptbr_clear_expressive"
    | "es_warm_direct"
    | "ar_warm_respectful"
    | "fil_warm_caring"
    | "id_warm_simple"
    | "ms_warm_polite"
    | "vi_warm_reflective";
  selectedCards: { card_name: string; isReversed: boolean }[];
  aiResponse: string;
}

const formatBirthDetailsForAPI = (d: BirthDetails): string => {
  let formatted = `Date of Birth: ${d.dob}`;
  if (d.birthTime) formatted += `, Time: ${d.birthTime}`;
  if (d.birthPlace) formatted += `, Place: ${d.birthPlace}`;
  return formatted;
};

// card_name -> image_url, built once from the shared tarot deck (same data
// TarotFanSpread draws from) so the result screen can show each drawn card's
// real art next to its meaning.
const CARD_IMAGE_BY_NAME: Record<string, string> = tarotCards.reduce(
  (map, c) => {
    map[c.card_name] = c.image_url;
    return map;
  },
  {} as Record<string, string>,
);

// The four card-position labels each backend prompt builder actually asks
// the model to use, per locale — mirrors OUTPUT_LABELS in
// helper/jpTarot/jpTarotPromptBuilder.js, POSITION_LABELS in
// helper/brTarot/brTarotPacks.js, and each lane's `positions` in
// helper/astriaTarotLanes/tarotLanesPacks.js. Used as a fallback heading
// matcher (see parseReadingSections) for models that write the label
// directly into the sentence with no colon/bold/markdown around it — e.g.
// "현재 상황 (현재 상황) 여제 카드가..." with no punctuation separating the
// label from the body, which no generic punctuation-based pattern can catch.
const POSITION_LABELS_BY_LOCALE: Record<Locale, string[]> = {
  "ja-JP": ["現在の状況", "内なる気持ち", "近い未来", "アドバイス"],
  "ko-KR": ["현재 상황", "내면의 감정", "가까운 미래", "조언"],
  "th-TH": ["สถานการณ์ตอนนี้", "ความรู้สึกภายใน", "ช่วงเวลาใกล้ๆ", "คำแนะนำ"],
  "en-US": ["Current State", "Inner Feelings", "Near Future", "Advice"],
  "pt-BR": ["Estado atual", "Sentimentos internos", "Tendência próxima", "Conselho"],
  es: ["Estado actual", "Sentimientos internos", "Tendencia próxima", "Consejo"],
  ar: ["الحالة الحالية", "المشاعر الداخلية", "الاتجاه القادم", "النصيحة"],
  fil: ["Kasalukuyang lagay", "Panloob na damdamin", "Paparating na tendensya", "Payo"],
  id: ["Keadaan saat ini", "Perasaan batin", "Kecenderungan berikutnya", "Saran"],
  ms: ["Keadaan semasa", "Perasaan dalaman", "Arah berikutnya", "Nasihat"],
  vi: ["Tình trạng hiện tại", "Cảm xúc bên trong", "Xu hướng sắp tới", "Lời khuyên"],
};

// Splits the backend's free-form aiResponse markdown into one chunk per
// drawn card, in order. The prompt builders (jpTarotPromptBuilder.js /
// brTarotPromptBuilder.js / tarotLanesPromptBuilder.js) all instruct the LLM
// to return "four short labeled sections" but don't pin an exact markdown
// syntax, and models are inconsistent about how they format the heading —
// some never separate it from the body with any punctuation at all (e.g.
// "현재 상황 (현재 상황) 여제 카드가..." as one continuous sentence, echoing
// the label the prompt gave it with no colon/bold/markdown around it). So
// this tries two passes, per line, in order:
//   1. Known-label match — strip a label this locale's prompt builder
//      actually asked for (POSITION_LABELS_BY_LOCALE) off the start of the
//      line, along with an optional "(label again)" echo right after it —
//      this is the reliable path and catches the punctuation-less case.
//   2. Punctuation heuristic — bold (**Label**), markdown heading (### /
//      ## / #), numbered ("1. Label"), or a short label ending in a colon —
//      for locales/models this page has no known-label table for, or a
//      heading the model invented that isn't one of the four asked-for ones.
// Either way, the heading can be alone on its own line or lead straight
// into the body text on the same line; whatever follows the heading on that
// line becomes the first line of the body. If the LLM doesn't format
// headings at all, falls back to treating the whole response as one block.
interface ParsedSection {
  heading: string;
  body: string;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseReadingSections(aiResponse: string, knownLabels: string[] = []): ParsedSection[] {
  const lines = aiResponse.split("\n");
  const sections: ParsedSection[] = [];
  let current: ParsedSection | null = null;

  // Known-label matcher: "<label>" optionally followed by "(<label or
  // anything short>)" and/or a colon, then the rest of the line as body.
  // Sorted longest-first so no label is a prefix-match of a longer one; the
  // sorted label text is kept alongside its compiled regex so the matched
  // heading can be read straight off without re-deriving it from the index.
  const knownLabelMatchers = knownLabels
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((label) => ({
      label,
      re: new RegExp(`^${escapeRegExp(label)}\\s*(?:\\([^)]{0,40}\\))?\\s*:?\\s*(.*)$`),
    }));

  // Punctuation-only heuristics, used when no known label matches this line.
  const BOLD_RE = /^\*\*(.{1,60}?)\*\*:?\s*(.*)$/;
  const MD_RE = /^#{1,4}\s+(.{1,60}?)\s*(?::\s*(.*))?$/;
  const NUMBERED_RE = /^\d+[.)]\s+\*{0,2}([^*:\n]{1,60}?)\*{0,2}:?\s*(.*)$/;
  const COLON_INLINE_RE = /^([^:\n]{1,40}):\s+(\S.*)$/;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    let heading: string | undefined;
    let inlineBody: string | undefined;

    for (const { label, re } of knownLabelMatchers) {
      const m = line.match(re);
      if (m) {
        heading = label;
        inlineBody = m[1];
        break;
      }
    }

    if (!heading) {
      const bold = line.match(BOLD_RE);
      const md = !bold ? line.match(MD_RE) : null;
      const numbered = !bold && !md ? line.match(NUMBERED_RE) : null;
      const bareColon =
        !bold && !md && !numbered && line.length <= 60 ? line.match(/^([^:]{1,60}):\s*$/) : null;
      const inlineColon = !bold && !md && !numbered && !bareColon ? line.match(COLON_INLINE_RE) : null;
      const match = bold || md || numbered || bareColon || inlineColon;
      heading = match?.[1];
      inlineBody = match?.[2];
    }

    if (heading) {
      current = { heading: heading.trim(), body: inlineBody ? inlineBody.trim() : "" };
      sections.push(current);
    } else if (current) {
      current.body += (current.body ? "\n" : "") + rawLine;
    } else {
      // Text before any recognized heading — keep as an unlabeled lead-in.
      current = { heading: "", body: rawLine };
      sections.push(current);
    }
  }

  return sections.filter((s) => s.body.trim().length > 0);
}

const LOCALE_STORAGE_KEY = "jpTarotLocale";

const JPTarotPage: React.FC = () => {
  const [locale, setLocale] = useState<Locale>(() => {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    return (LOCALES as string[]).includes(saved || "")
      ? (saved as Locale)
      : "ja-JP";
  });
  const copy = COPY[locale];

  const [step, setStep] = useState<Step>("questionType");
  const [category, setCategory] = useState<Category>("general");
  const [questionType, setQuestionType] = useState<QuestionType>("message_now");
  const [birthDetails, setBirthDetails] = useState<BirthDetails | null>(() => {
    try {
      const saved = localStorage.getItem("userBirthDetails");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [showBirthPopup, setShowBirthPopup] = useState(false);
  const [result, setResult] = useState<CardReadingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLocaleChange = (next: Locale) => {
    setLocale(next);
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
  };

  const handleCategoryChange = (next: Category) => {
    setCategory(next);
    setQuestionType(questionTypesForLocale(locale)[next][0]);
  };

  const goToCards = () => {
    if (!birthDetails) {
      setShowBirthPopup(true);
      return;
    }
    setStep("cards");
  };

  const handleBirthSaved = (details: BirthDetails) => {
    localStorage.setItem("userBirthDetails", JSON.stringify(details));
    setBirthDetails(details);
    setShowBirthPopup(false);
    setStep("cards");
  };

  const handleGetReading = async (cards: TarotCard[], question: string) => {
    setLoading(true);
    setError(null);
    try {
      const isBR = BR_LOCALES.has(locale);
      const isLane = LANE_LOCALES.has(locale);
      // pt-BR is served by its own standalone pack (helper/brTarot/*), not
      // the jp-tarot locale bridge — it has no `locale` field (always pt-BR
      // server-side) and its own endpoint. The 6 Astria Tarot lanes
      // (helper/astriaTarotLanes/*) are also standalone, but their endpoint
      // requires `locale` in the body to pick the lane.
      const payload = {
        userId: localStorage.getItem("userId") || undefined,
        sessionId: sessionStorage.getItem("jpTarotSessionId") || undefined,
        questionType,
        ...(isBR ? {} : { locale }),
        userMessage: question || copy.defaultQuestionPrompt,
        memory: birthDetails ? formatBirthDetailsForAPI(birthDetails) : "",
        selectedCards: cards.map((c) => ({
          card_name: c.card_name,
          isReversed: false,
        })),
      };
      const endpoint = isBR
        ? "/backend/br-tarot/card-reading"
        : isLane
          ? "/backend/tarot-lanes/card-reading"
          : "/backend/jp-tarot/card-reading";
      const response = await api.post(endpoint, payload);
      if (response.data?.success) {
        setResult(response.data);
        setStep("result");
      } else {
        setError(response.data?.message || copy.fallbackErrorMessage);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.fallbackResponse ||
          copy.fallbackErrorMessage,
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setStep("questionType");
  };

  const isRtl = RTL_LOCALES.has(locale);

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="min-h-screen px-5 sm:px-10 lg:px-16 py-6 sm:py-10"
      style={{ background: C.bg, fontFamily: FONT }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full mx-auto"
        style={{
          maxWidth: step === "questionType" ? 720 : 1040,
          transition: "max-width 0.3s ease",
        }}
      >
        {/* Header: brand mark + title, language dropdown */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: `radial-gradient(120% 140% at 30% 20%, rgba(255,255,255,0.35), transparent 55%), linear-gradient(155deg, ${C.accent} 0%, ${C.accentDeep} 100%)`,
                boxShadow: `0 6px 16px -6px ${C.accentDeep}88`,
              }}
            >
              <Sparkle className="w-4 h-4" style={{ color: "#FFFFFF" }} strokeWidth={2} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: C.muted,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontFamily: FONT_BODY,
                }}
              >
                {copy.badge}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: C.text,
                  fontFamily: FONT_DISPLAY,
                  marginTop: 1,
                }}
              >
                {copy.badgeSub}
              </div>
            </div>
          </div>

          <div className="relative self-start sm:self-auto" style={{ flexShrink: 0 }}>
            <select
              aria-label="Language"
              value={locale}
              onChange={(e) => handleLocaleChange(e.target.value as Locale)}
              className="appearance-none pr-8 pl-3 py-2 text-[12.5px] font-semibold transition-all duration-200 cursor-pointer"
              style={{
                borderRadius: 10,
                border: `1px solid ${C.lineStrong}`,
                background: C.panel,
                color: C.text,
                fontFamily: FONT_BODY,
              }}
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {LOCALE_FLAG[l]} {LOCALE_FULL_LABEL[l]}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
              style={{ color: C.muted }}
            />
          </div>
        </div>

        <StepRail step={step} stepLabel={copy.stepLabel} />

        <AnimatePresence mode="wait">
          {step === "questionType" && (
            <QuestionTypeStep
              key="questionType"
              category={category}
              questionType={questionType}
              copy={copy}
              questionTypes={questionTypesForLocale(locale)}
              onCategoryChange={handleCategoryChange}
              onQuestionTypeChange={setQuestionType}
              onNext={goToCards}
            />
          )}

          {step === "cards" && (
            <motion.div
              key="cards"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                onClick={() => setStep("questionType")}
                className="flex items-center gap-1.5 text-xs font-medium mb-3"
                style={{ color: C.muted, fontFamily: FONT_BODY }}
              >
                <ArrowLeft className="w-3.5 h-3.5" style={isRtl ? { transform: "scaleX(-1)" } : undefined} />
                {copy.backButton}
              </button>
              <TarotFanSpread
                category={category}
                isLoading={loading}
                onGetReading={handleGetReading}
                theme={FAN_SPREAD_THEME}
                useAstriaCardBack
              />
            </motion.div>
          )}

          {step === "result" && result && (
            <ResultView key="result" result={result} copy={copy} locale={locale} isRtl={isRtl} onBack={reset} />
          )}
        </AnimatePresence>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-sm flex items-center gap-2"
            style={{ color: C.error, fontFamily: FONT_BODY }}
          >
            <span className="text-base">✧</span>
            {error}
          </motion.p>
        )}
      </motion.div>

      <BirthDetailsPopup
        isOpen={showBirthPopup}
        onClose={() => setShowBirthPopup(false)}
        onSave={handleBirthSaved}
        mainCategory="Astria Japan"
      />
    </div>
  );
};

const QuestionTypeStep: React.FC<{
  category: Category;
  questionType: QuestionType;
  copy: LocaleCopy;
  questionTypes: Record<Category, QuestionType[]>;
  onCategoryChange: (c: Category) => void;
  onQuestionTypeChange: (q: QuestionType) => void;
  onNext: () => void;
}> = ({
  category,
  questionType,
  copy,
  questionTypes,
  onCategoryChange,
  onQuestionTypeChange,
  onNext,
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div
      className="text-[21px] sm:text-[25px]"
      style={{
        fontWeight: 500,
        color: C.text,
        marginBottom: 6,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        fontFamily: FONT_DISPLAY,
      }}
    >
      {copy.heading}
    </div>
    <p
      className="text-[13px] sm:text-[13.5px] mb-6"
      style={{ color: C.muted, lineHeight: 1.65, fontFamily: FONT_BODY }}
    >
      {copy.subheading}
    </p>

    <div
      className="relative"
      style={{
        background: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 18,
        padding: "20px 18px",
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.12em",
          color: C.muted,
          marginBottom: 10,
          textTransform: "uppercase",
          fontFamily: FONT_BODY,
        }}
      >
        {copy.categoryHeader}
      </div>
      <div className="flex gap-1.5 sm:gap-2 mb-5">
        {(Object.keys(copy.categoryLabel) as Category[]).map((key) => {
          const active = key === category;
          return (
            <button
              key={key}
              onClick={() => onCategoryChange(key)}
              className="flex-1 py-2.5 px-1 text-[11.5px] sm:text-xs font-semibold transition-all duration-200"
              style={{
                background: active ? C.accent : C.panelAlt,
                color: active ? "#FFFFFF" : C.text,
                border: `1px solid ${active ? C.accent : C.lineStrong}`,
                borderRadius: 11,
                fontFamily: FONT_BODY,
              }}
            >
              {copy.categoryLabel[key]}
            </button>
          );
        })}
      </div>

      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.12em",
          color: C.muted,
          marginBottom: 10,
          textTransform: "uppercase",
          fontFamily: FONT_BODY,
        }}
      >
        {copy.questionHeader}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
        {questionTypes[category].map((opt) => {
          const active = opt === questionType;
          return (
            <button
              key={opt}
              onClick={() => onQuestionTypeChange(opt)}
              className="text-left px-3.5 py-3 text-[12.5px] font-medium transition-all duration-200"
              style={{
                borderRadius: 12,
                border: `1px solid ${active ? C.accent : C.lineStrong}`,
                background: active ? C.accentSoft : C.panelAlt,
                color: active ? C.accentDeep : C.text,
                fontFamily: FONT_BODY,
                lineHeight: 1.4,
              }}
            >
              {copy.questionLabel[opt]}
            </button>
          );
        })}
      </div>
    </div>

    <motion.button
      onClick={onNext}
      className="mt-6 w-full py-3.5 text-sm font-semibold transition-all duration-200"
      style={{
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDeep} 100%)`,
        boxShadow: `0 10px 24px -10px ${C.accent}66`,
        color: "#FFFFFF",
        borderRadius: 12,
        fontFamily: FONT_BODY,
        letterSpacing: "0.02em",
      }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
    >
      {copy.chooseCardsButton}
    </motion.button>
  </motion.div>
);

// Result screen: parses the backend's free-form aiResponse into one section
// per drawn card (see parseReadingSections above), shows each alongside its
// real card art in a numbered row, and closes with an "Overall Message"
// panel (reusing the reading's last/advice section — the LLM's own
// synthesizing thought, not new content) and a static "Reflection" prompt.
// If parsing can't confidently split into per-card sections (fewer sections
// than cards, or the LLM returned unlabeled prose), falls back to rendering
// aiResponse as one markdown block so nothing is ever lost or misattributed.
const ResultView: React.FC<{
  result: CardReadingResult;
  copy: LocaleCopy;
  locale: Locale;
  isRtl: boolean;
  onBack: () => void;
}> = ({ result, copy, locale, isRtl, onBack }) => {
  const sections = parseReadingSections(result.aiResponse, POSITION_LABELS_BY_LOCALE[locale]);
  const cards = result.selectedCards;
  const canPairByCard = sections.length >= cards.length && cards.length > 0;
  const cardSections = canPairByCard ? sections.slice(0, cards.length) : [];
  const overallSection = canPairByCard ? sections[sections.length - 1] : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: isRtl ? -12 : 12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
    >
      <div className="mb-5">
        <div
          className="text-[19px] sm:text-[21px]"
          style={{
            fontWeight: 500,
            color: C.text,
            marginBottom: 4,
            fontFamily: FONT_DISPLAY,
            lineHeight: 1.35,
          }}
        >
          {copy.questionLabel[result.questionType as QuestionType] || result.questionType}
        </div>
      </div>

      {canPairByCard ? (
        <div>
          {cards.map((c, i) => {
            const section = cardSections[i];
            const imageUrl = CARD_IMAGE_BY_NAME[c.card_name];
            return (
              <div
                key={`${c.card_name}-${i}`}
                className="flex flex-row gap-3 sm:gap-5 items-start mb-3.5"
                style={{
                  background: C.accentSoft,
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                {imageUrl && (
                  <div
                    className="shrink-0 overflow-hidden w-14 xs:w-16 sm:w-20"
                    style={{
                      maxWidth: "30%",
                      aspectRatio: "3 / 4",
                      borderRadius: 8,
                      border: `1px solid ${C.lineStrong}`,
                      boxShadow: "0 4px 10px rgba(30,20,70,0.12)",
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={c.card_name}
                      className="block w-full h-full object-cover"
                      style={c.isReversed ? { transform: "rotate(180deg)" } : undefined}
                    />
                  </div>
                )}
                <div className="min-w-0 w-full flex-1">
                  <div
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: C.muted,
                      marginBottom: 4,
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {`${copy.stepLabel.cards} ${i + 1}`}
                  </div>
                  <div
                    className="uppercase"
                    style={{
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: C.text,
                      marginBottom: 8,
                      letterSpacing: "0.01em",
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {c.card_name}
                    {c.isReversed ? ` (${copy.reversedLabel})` : ""}
                    {section?.heading && (
                      <span
                        className="normal-case"
                        style={{ fontWeight: 500, color: C.accentDeep }}
                      >
                        {" "}
                        ({section.heading})
                      </span>
                    )}
                  </div>
                  <div
                    className="prose prose-sm max-w-none break-words"
                    style={{
                      fontSize: 13,
                      lineHeight: 1.7,
                      color: C.muted,
                      fontFamily: FONT_BODY,
                      overflowWrap: "break-word",
                    }}
                  >
                    <ReactMarkdown>{section?.body || ""}</ReactMarkdown>
                  </div>
                </div>
              </div>
            );
          })}

          {overallSection && (
            <div
              className="mb-3.5"
              style={{
                background: C.panel,
                border: `1px solid ${C.line}`,
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkle className="w-4 h-4" style={{ color: C.accentDeep }} strokeWidth={2} />
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: C.text,
                    fontFamily: FONT_BODY,
                  }}
                >
                  {copy.overallMessageLabel}
                </span>
              </div>
              <div
                className="prose prose-sm max-w-none break-words"
                style={{
                  fontSize: 13,
                  lineHeight: 1.7,
                  color: C.muted,
                  fontFamily: FONT_BODY,
                  overflowWrap: "break-word",
                }}
              >
                <ReactMarkdown>{overallSection.body}</ReactMarkdown>
              </div>
            </div>
          )}

          <div
            className="mb-4"
            style={{
              background: C.panel,
              border: `1px solid ${C.line}`,
              borderRadius: 16,
              padding: 18,
            }}
          >
            <div className="flex items-center gap-2 mb-2.5">
              <PenLine className="w-3.5 h-3.5" style={{ color: C.accentDeep }} strokeWidth={2} />
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: C.text,
                  fontFamily: FONT_BODY,
                }}
              >
                {copy.reflectionLabel}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.7,
                color: C.muted,
                fontFamily: FONT_BODY,
              }}
            >
              {copy.reflectionPrompt}
            </div>
          </div>

          <button
            onClick={onBack}
            className="w-full py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
            style={{
              borderRadius: 12,
              border: `1px solid ${C.lineStrong}`,
              color: C.text,
              fontFamily: FONT_BODY,
              background: C.panel,
            }}
          >
            <RotateCcw className="w-4 h-4" style={isRtl ? { transform: "scaleX(-1)" } : undefined} />
            {copy.askAgainButton}
          </button>
        </div>
      ) : (
        // Fallback: the LLM's response couldn't be confidently split into
        // one section per card — render it as a single reading block instead
        // of guessing at a pairing that might misattribute text to a card.
        <>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {cards.map((c, i) => (
              <span
                key={`${c.card_name}-${i}`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.accentDeep,
                  background: C.accentSoft,
                  border: `1px solid ${C.accent}33`,
                  borderRadius: 7,
                  padding: "4px 10px",
                  fontFamily: FONT_BODY,
                }}
              >
                {c.card_name}
              </span>
            ))}
          </div>
          <div
            className="relative prose prose-sm max-w-none px-5 py-6 text-[14px] sm:text-[15px] break-words"
            style={{
              background: `radial-gradient(140% 100% at ${isRtl ? "100% 0%" : "0% 0%"}, ${C.accentSoft}, transparent 60%), ${C.panelAlt}`,
              borderRadius: 16,
              border: `1px solid ${C.line}`,
              color: C.text,
              lineHeight: 1.85,
              fontFamily: FONT_BODY,
              overflowWrap: "break-word",
            }}
          >
            <ReactMarkdown>{result.aiResponse}</ReactMarkdown>
          </div>
          <button
            onClick={onBack}
            className="mt-6 w-full py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
            style={{
              borderRadius: 12,
              border: `1px solid ${C.lineStrong}`,
              color: C.text,
              fontFamily: FONT_BODY,
              background: C.panel,
            }}
          >
            <RotateCcw className="w-4 h-4" style={isRtl ? { transform: "scaleX(-1)" } : undefined} />
            {copy.askAgainButton}
          </button>
        </>
      )}
    </motion.div>
  );
};

export default JPTarotPage;
